// Reusable Button component
import { ButtonHTMLAttributes, forwardRef } from 'react';
import { cn } from '@utils/classNames';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'md', isLoading, children, disabled, ...props }, ref) => {
    return (
      <button
        ref={ref}
        className={cn(
          'inline-flex items-center justify-center rounded-[15px] font-codec font-bold transition-all',
          'disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-0',
          {
            // Variants
            'bg-gradient-primary shadow-lg shadow-primary/20 text-black hover:bg-gradient-primary-hover hover:scale-105 hover:shadow-primary/30':
              variant === 'primary',
            'bg-white/10 text-white hover:bg-white/20 border border-white/10': variant === 'secondary',
            'border border-red-500/40 bg-red-500/10 text-red-200 shadow-none hover:bg-red-500/[0.18] hover:border-red-500/55 hover:scale-100':
              variant === 'danger',
            'bg-transparent hover:bg-white/10': variant === 'ghost',
            
            // Sizes
            'h-[45px] px-6 text-xl': size === 'lg',
            'h-[41px] px-5 text-xl': size === 'md',
            'h-[30px] px-4 text-base': size === 'sm',
          },
          className
        )}
        disabled={disabled || isLoading}
        {...props}
      >
        {isLoading ? (
          <span className="flex items-center gap-2.5">
            <div className="relative flex items-center justify-center">
              {/* Spinning ring */}
              <div className="absolute w-4 h-4 border-2 border-current/30 border-t-current rounded-full animate-spin-slow" />
              {/* Inner pulse */}
              <div className="w-2 h-2 bg-current rounded-full animate-pulse opacity-60" />
            </div>
            <span className="font-codec">Loading...</span>
          </span>
        ) : (
          children
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';

